import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Building2, Save } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSchoolForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [createdSchool, setCreatedSchool] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    head_coach_name: '',
    phone: '',
    email: '',
    division: '',
    district: '',
    training_address: '',
    gelanggang: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/schools/admin-create`, formData);
      setCreatedSchool(response.data);
      
      // Fetch approval info to get temp password
      try {
        const approvalRes = await axios.get(`${API_URL}/approvals/school/${response.data.id}`);
        if (approvalRes.data && approvalRes.data.temp_password) {
          setCredentials({
            email: formData.email,
            password: approvalRes.data.temp_password
          });
        }
      } catch (err) {
        console.log('Could not fetch credentials');
      }
      
      toast.success('Perguruan berjaya didaftarkan!');
    } catch (error) {
      console.error('Failed to create school:', error);
      toast.error(error.response?.data?.detail || 'Gagal mendaftarkan perguruan');
    } finally {
      setLoading(false);
    }
  };

  if (createdSchool) {
    return (
      <AdminLayout>
        <div className="space-y-6 max-w-4xl">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/schools')}
            className="border-stone-200"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Kembali ke Senarai
          </Button>

          <Alert className="bg-green-50 border-2 border-green-300">
            <AlertDescription>
              <div className="space-y-4">
                <h3 className="text-lg font-bold text-green-800">Perguruan Berjaya Didaftarkan!</h3>
                <div className="bg-white rounded-md p-4 border border-green-200">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-[#78716C]">Nama Perguruan:</span>
                      <span className="font-semibold text-[#1A1A1A]">{createdSchool.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#78716C]">Bahagian / Daerah:</span>
                      <span className="font-semibold text-[#1A1A1A]">{createdSchool.division}, {createdSchool.district}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#78716C]">Status:</span>
                      <span className="px-2 py-1 rounded text-sm font-medium bg-green-100 text-green-800 border border-green-200">
                        {createdSchool.status}
                      </span>
                    </div>
                  </div>
                  
                  {credentials && (
                    <div className="mt-4 pt-4 border-t border-green-200">
                      <p className="text-sm font-semibold text-green-800 mb-2">Maklumat Login SCHOOL_ADMIN:</p>
                      <div className="space-y-2 font-mono text-sm">
                        <div className="flex justify-between">
                          <span>Emel:</span>
                          <span className="font-semibold">{credentials.email}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Password:</span>
                          <span className="font-semibold">{credentials.password}</span>
                        </div>
                      </div>
                      <p className="text-xs text-[#78716C] mt-2">
                        ✉️ Simpan maklumat ini dan kongsikan dengan perguruan
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="flex gap-3">
                  <Button
                    onClick={() => {
                      setCreatedSchool(null);
                      setCredentials(null);
                      setFormData({
                        name: '',
                        head_coach_name: '',
                        phone: '',
                        email: '',
                        division: '',
                        district: '',
                        training_address: '',
                        gelanggang: ''
                      });
                    }}
                    className="bg-[#D71920] hover:bg-[#b8161c]"
                  >
                    Daftar Perguruan Lain
                  </Button>
                  <Button
                    onClick={() => navigate('/admin/schools')}
                    variant="outline"
                  >
                    Lihat Senarai Perguruan
                  </Button>
                </div>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-4xl">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/schools')}
            className="border-stone-200"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Kembali
          </Button>
        </div>

        <div className="flex items-center space-x-4">
          <div className="w-10 h-10 rounded bg-[#D71920]/10 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-[#D71920]" />
          </div>
          <div>
            <div>
              <div className="text-xs font-bold text-[#D71920] uppercase tracking-widest mb-1">Pentadbiran</div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0B0B0B]" data-testid="page-title">
                Daftar Perguruan Baru
              </h1>
              <p className="text-[#777] text-sm mt-0.5">Admin RSS mendaftarkan perguruan secara langsung</p>
            </div>
          </div>
        </div>

        <div className="h-px bg-[#E5E5E5]" />

        <Alert className="border-[#0369A1] bg-blue-50">
          <AlertDescription className="text-[#0369A1]">
            Perguruan yang didaftarkan oleh admin akan mempunyai status <strong>Active</strong> secara automatik. 
            Sistem akan cipta akaun SCHOOL_ADMIN untuk perguruan ini.
          </AlertDescription>
        </Alert>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-stone-200 p-8 space-y-6" data-testid="admin-school-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <Label htmlFor="name">Nama Perguruan *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                
                className="border-stone-200"
                data-testid="input-name"
              />
            </div>

            <div>
              <Label htmlFor="head_coach_name">Ketua Jurulatih *</Label>
              <Input
                id="head_coach_name"
                value={formData.head_coach_name}
                onChange={(e) => setFormData({ ...formData, head_coach_name: e.target.value })}
                required
                className="border-stone-200"
                data-testid="input-coach"
              />
            </div>

            <div>
              <Label htmlFor="phone">Telefon *</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                className="border-stone-200"
                data-testid="input-phone"
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="email">Emel *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="border-stone-200"
                data-testid="input-email"
              />
              <p className="text-xs text-[#78716C] mt-1">
                Emel ini akan digunakan untuk login SCHOOL_ADMIN
              </p>
            </div>

            <div>
              <Label htmlFor="division">Bahagian *</Label>
              <Input
                id="division"
                value={formData.division}
                onChange={(e) => setFormData({ ...formData, division: e.target.value })}
                required
                className="border-stone-200"
                data-testid="input-division"
              />
            </div>

            <div>
              <Label htmlFor="district">Daerah *</Label>
              <Input
                id="district"
                value={formData.district}
                onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                required
                className="border-stone-200"
                data-testid="input-district"
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="training_address">Alamat Tempat Latihan *</Label>
              <Input
                id="training_address"
                value={formData.training_address}
                onChange={(e) => setFormData({ ...formData, training_address: e.target.value })}
                required
                className="border-stone-200"
                data-testid="input-address"
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="gelanggang">Nama Gelanggang <span className="text-xs font-normal text-[#999]">(Opsional)</span></Label>
              <Input
                id="gelanggang"
                value={formData.gelanggang}
                onChange={(e) => setFormData({ ...formData, gelanggang: e.target.value })}
                placeholder="Contoh: Dewan Serbaguna Kampung Buntal"
                className="border-stone-200"
                data-testid="input-gelanggang"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-6 border-t border-stone-200">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/schools')}
              className="border-stone-200"
              data-testid="cancel-button"
            >
              Batal
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-[#D71920] hover:bg-[#b8161c] text-white px-8"
              data-testid="submit-button"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Mendaftarkan...' : 'Daftar Perguruan'}
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AdminSchoolForm;
